import { useCallback, useMemo } from 'react';
import {
  combineMessage,
  getCellWidths,
  getColumnByType,
  getRowHeight,
  updateDataByType
} from '../utils/functions';
import commonStore from '../../../../store/common';
import { CustomHeaderWithDrillDown } from '../../components/CustomHeaderWithDrillDown';
import { useSearchParams } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { isEsmaTableDisabledForFund } from '../utils/visibleSections';

const columnLables = {
  africa: {
    type: 'number',
    name: 'Africa NAV percentage',
    order: 78
  },
  asia_pacific: {
    type: 'number',
    name: 'Asia Pacific (other than Middle East) NAV percentage',
    order: 79
  },
  europe: {
    type: 'number',
    name: 'Europe (other than EEA) NAV percentage',
    order: 80
  },
  eea: {
    type: 'number',
    name: 'Europe EEA NAV percentage',
    order: 81
  },
  middle_east: {
    type: 'number',
    name: 'Middle East NAV percentage',
    order: 82
  },
  north_america: {
    type: 'number',
    name: 'North America NAV percentage',
    order: 83
  },
  south_america: {
    type: 'number',
    name: 'South America NAV percentage',
    order: 84
  },
  supra_national: {
    type: 'number',
    name: 'Supra National/Multiple regions NAV percentage',
    order: 85
  }
};

const _tables = [
  {
    title: 'Percentage of NAV',
    key: 'nav_rate',
    columnLables: {
      africa: {
        type: 'number',
        name: 'Africa NAV percentage',
        order: 78
      },
      asia_pacific: {
        type: 'number',
        name: 'Asia Pacific (other than Middle East) NAV percentage',
        order: 79
      },
      europe: {
        type: 'number',
        name: 'Europe (other than EEA) NAV percentage',
        order: 80
      },
      eea: {
        type: 'number',
        name: 'Europe EEA NAV percentage',
        order: 81
      },
      middle_east: {
        type: 'number',
        name: 'Middle East NAV percentage',
        order: 82
      },
      north_america: {
        type: 'number',
        name: 'North America NAV percentage',
        order: 83
      },
      south_america: {
        type: 'number',
        name: 'South America NAV percentage',
        order: 84
      },
      supra_national: {
        type: 'number',
        name: 'Supra National/Multiple regions NAV percentage',
        order: 85
      }
    }
  },
  {
    title: 'Percentage of Aggregated value *',
    key: 'aum_rate',
    columnLables: {
      africa: {
        type: 'number',
        name: 'Africa AuM value percentage *',
        order: 86
      },
      asia_pacific: {
        type: 'number',
        name: 'Asia Pacific (other than Middle East) AuM value percentage *',
        order: 87
      },
      europe: {
        type: 'number',
        name: 'Europe (other than EEA) AuM value percentage *',
        order: 88
      },
      eea: {
        type: 'number',
        name: 'Europe EEA AuM value percentage *',
        order: 89
      },
      middle_east: {
        type: 'number',
        name: 'Middle East AuM value percentage *',
        order: 90
      },
      north_america: {
        type: 'number',
        name: 'North America AuM value percentage *',
        order: 91
      },
      south_america: {
        type: 'number',
        name: 'South America AuM value percentage *',
        order: 92
      },
      supra_national: {
        type: 'number',
        name: 'Supra National/Multiple regions AuM value percentage *',
        order: 93
      }
    }
  }
];

export default function useGeographicalFocus({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  funds,
  isMultiFunds
}) {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const currentDataKey = type === 'AIF001' ? 'AIFOO1' : searchParams.get('fund_id');
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isEsmaTableDisabledForFund(fund, 'GEOGRAPHICAL_FOCUS');
  };

  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );
  const onChange = useCallback(
    value => {
      const changes = value[0];
      const fund = isMultiFunds
        ? JSON.parse(JSON.stringify(funds[changes.newCell.fundId].fund_data['GEOGRAPHICAL_FOCUS']))
        : JSON.parse(JSON.stringify(data));

      fund[changes.newCell.dataIndex][changes.newCell.dataKey] = updateDataByType({
        cell: changes.newCell
      });

      onChangeFund('GEOGRAPHICAL_FOCUS', fund, changes.newCell.fundId);
    },
    [data]
  );

  const columns = useMemo(() => {
    let _columns = [];
    if (!isMultiFunds) {
      _columns = Array.from(Array(isComparisonReport ? 3 : 2).keys()).map(index => ({
        columnId: index + 1,
        width: contentWidth / (isComparisonReport ? 3 : 2)
      }));
    } else {
      const _fundSize = Object.keys(funds).length;
      _columns =
        _fundSize <= 3 && !isComparisonReport
          ? Array.from(Array(_fundSize + 1).keys()).map(index => ({
              columnId: index + 1,
              width: contentWidth / (_fundSize + 1)
            }))
          : isComparisonReport
            ? [
                {
                  columnId: 1,
                  width: 200
                },
                ...Object.keys(funds)
                  .map(item => [item, item.previous_fund])
                  .flat()
                  .map((_, index) => ({
                    columnId: index + 2,
                    width: 300
                  }))
              ]
            : [
                {
                  columnId: 1,
                  width: 200
                },
                ...Object.keys(funds).map((_, index) => ({
                  columnId: index + 2,
                  width: 300
                }))
              ];
    }
    return [
      {
        columnId: 0,
        resizable: true,
        width: 30
      },
      ..._columns
    ];
  }, [isMultiFunds, funds, contentWidth, isComparisonReport]);

  const tables = useMemo(() => {
    if (!data) return [];

    const { labelWidth, valueWidth } = getCellWidths({
      isMultiFunds,
      fundSize: Object.keys(funds).length,
      contentWidth,
      isComparisonReport
    });

    return _tables.map(value => {
      let _rows = [];
      if (!isMultiFunds) {
        _rows = [
          {
            rowId: 0,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: value.title
              },
              ...(isComparisonReport
                ? [
                    {
                      type: 'header',
                      text: `${funds[currentDataKey].previous_fund?.period}, ${funds[currentDataKey].previous_fund?.year}`
                    },
                    {
                      type: 'header',
                      text: `${funds[currentDataKey].period}, ${funds[currentDataKey].year}`
                    }
                  ]
                : [
                    {
                      type: 'header',
                      text: ''
                    }
                  ])
            ]
          }
        ];
      } else {
        _rows = [
          {
            rowId: 0,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: value.title
              },
              ...(isComparisonReport
                ? Object.values(funds)
                    .map(item => [item, item.previous_fund])
                    .flat()
                    .map((value, j) => ({
                      type: 'header',
                      text: value?.fund_name || '',
                      colspan: j % 2 === 0 ? 2 : undefined
                    }))
                : Object.values(funds).map(value => ({
                    type: 'header',
                    text: value?.fund_name || ''
                  })))
            ]
          },
          ...(isComparisonReport && isMultiFunds
            ? [
                {
                  rowId: 1,
                  height: 40,
                  cells: [
                    {
                      type: 'header',
                      text: ''
                    },
                    {
                      type: 'header',
                      text: ''
                    },
                    ...Object.values(funds)
                      .map(item => [item.previous_fund, item])
                      .flat()
                      .map(value => ({
                        type: 'header',
                        text: `${value.period}, ${value.year}`
                      }))
                  ]
                }
              ]
            : [])
        ];
      }
      return [
        ..._rows,
        ...Object.keys(value.columnLables).map((key, index) => {
          let cells = [];
          if (isMultiFunds) {
            cells = isComparisonReport
              ? Object.values(funds)
                  .map(item => [item.previous_fund, item])
                  .flat()
                  .map((item, j) =>
                    getColumnByType({
                      type: schema.types[`${key}_${value.key}`]?.type,
                      options: schema.types[`${key}_${value.key}`].options,
                      key: `${key}_${value.key}`,
                      defaultValue: schema.defaultValues[`${key}_${value.key}`],
                      index: 0,
                      value: item.fund_data.GEOGRAPHICAL_FOCUS[0],
                      required: requiredFields.includes(`${key}_${value.key}`),
                      tableName: 'GEOGRAPHICAL_FOCUS',
                      isStaticTemplate,
                      fundId: item.fund_id,
                      isSectionDisabled: isTableDisabledForFund(item.fund_id),
                      fundName: item.fund_name,
                      hasDrillDownFeature:
                        !!item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.report_id &&
                        item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.[`${key}_${value.key}`] !== null &&
                        schema.types[`${key}_${value.key}`].has_drill_down_feature,
                      reportId: item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.report_id,
                      tableId: item.fund_data['GEOGRAPHICAL_FOCUS']?.[0]?.id,
                      title: `${columnLables[key].name} ${value.title}`,
                      isMultiFunds,
                      isEsma: true,
                      fieldOrder: columnLables[key]?.order,
                      difference:
                        j % 2 === 1
                          ? funds[item.fund_id]?.difference?.[`GEOGRAPHICAL_FOCUS`]?.[0]?.[
                              `${key}_${value.key}`
                            ] || {}
                          : null,
                      nonEditable: isComparisonReport
                    })
                  )
              : Object.values(funds).map(item =>
                  getColumnByType({
                    type: schema.types[`${key}_${value.key}`].type,
                    options: schema.types[`${key}_${value.key}`].options,
                    key: `${key}_${value.key}`,
                    defaultValue: schema.defaultValues[`${key}_${value.key}`],
                    index: 0,
                    value: item.fund_data.GEOGRAPHICAL_FOCUS[0],
                    required: requiredFields.includes(`${key}_${value.key}`),
                    tableName: 'GEOGRAPHICAL_FOCUS',
                    isStaticTemplate,
                    fundId: item.fund_id,
                    isSectionDisabled: isTableDisabledForFund(item.fund_id),
                    fundName: item.fund_name,
                    hasDrillDownFeature:
                      !!item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.report_id &&
                      item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.[`${key}_${value.key}`] !== null &&
                      schema.types[`${key}_${value.key}`].has_drill_down_feature,
                    reportId: item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.report_id,
                    tableId: item.fund_data['GEOGRAPHICAL_FOCUS']?.[0]?.id,
                    title: `${columnLables[key].name} ${value.title}`,
                    isMultiFunds,
                    isEsma: true,
                    fieldOrder: columnLables[key]?.order,
                    nonEditable: isComparisonReport
                  })
                );
          } else {
            cells = [
              ...(isComparisonReport
                ? [
                    getColumnByType({
                      type: schema.types[`${key}_${value.key}`].type,
                      options: schema.types[`${key}_${value.key}`].options,
                      key: `${key}_${value.key}`,
                      defaultValue: schema.defaultValues[`${key}_${value.key}`],
                      index: 0,
                      value: funds[currentDataKey].previous_fund.fund_data['GEOGRAPHICAL_FOCUS'][0],
                      required: requiredFields.includes(`${key}_${value.key}`),
                      tableName: 'GEOGRAPHICAL_FOCUS',
                      isStaticTemplate,
                      fundId: funds[currentDataKey].previous_fund.fund_id,
                      isSectionDisabled: isTableDisabledForFund(
                        funds[currentDataKey].previous_fund
                      ),
                      fundName: funds[currentDataKey].previous_fund.fund_name,
                      hasDrillDownFeature:
                        !!funds[currentDataKey].previous_fund.fund_data['GEOGRAPHICAL_FOCUS'][0]
                          ?.report_id &&
                        funds[currentDataKey].previous_fund.fund_data['GEOGRAPHICAL_FOCUS'][0]?.[
                          `${key}_${value.key}`
                        ] !== null &&
                        schema.types[`${key}_${value.key}`].has_drill_down_feature,
                      reportId:
                        funds[currentDataKey].previous_fund.fund_data['GEOGRAPHICAL_FOCUS'][0]
                          ?.report_id,
                      tableId:
                        funds[currentDataKey].previous_fund.fund_data['GEOGRAPHICAL_FOCUS']?.[0]
                          ?.id,
                      title: `${columnLables[key].name} ${value.title}`,
                      isMultiFunds,
                      isEsma: true,
                      fieldOrder: columnLables[key]?.order,
                      nonEditable: isComparisonReport
                    })
                  ]
                : []),
              getColumnByType({
                type: schema.types[`${key}_${value.key}`].type,
                options: schema.types[`${key}_${value.key}`].options,
                key: `${key}_${value.key}`,
                defaultValue: schema.defaultValues[`${key}_${value.key}`],
                index: 0,
                value: data[0],
                required: requiredFields.includes(`${key}_${value.key}`),
                tableName: 'GEOGRAPHICAL_FOCUS',
                isStaticTemplate,
                isMultiFunds,
                isEsma: true,
                fundId: data[0]?.fund_id,
                isSectionDisabled: isTableDisabledForFund(data[0]?.fund_id),
                fundName: data[0].fund_name,
                fieldOrder: columnLables[key]?.order,
                nonEditable: isComparisonReport,
                difference: isComparisonReport
                  ? funds[currentDataKey]?.difference?.[`GEOGRAPHICAL_FOCUS`]?.[0]?.[
                      `${key}_${value.key}`
                    ] || {}
                  : null
              })
            ];
          }

          const fieldKey = `${key}_${value.key}`;
          const isRequired = requiredFields.includes(fieldKey);

          // Each value column stacks its own value above its own error, so the
          // row must fit the tallest column — pairing the longest value with a
          // different column's longest error over-sizes it.
          const rows = isMultiFunds
            ? isComparisonReport
              ? Object.values(funds)
                  .map(fund => [fund.previous_fund, fund])
                  .flat()
                  .map(fund => fund.fund_data['GEOGRAPHICAL_FOCUS'][0])
              : Object.values(funds).map(fund => fund.fund_data['GEOGRAPHICAL_FOCUS'][0])
            : isComparisonReport
              ? [funds[currentDataKey].previous_fund.fund_data['GEOGRAPHICAL_FOCUS'][0], data[0]]
              : [data[0]];

          const valueCells = rows.map(row => {
            const fieldValue = row?.[fieldKey];
            const isEmpty = fieldValue === null || fieldValue === undefined || fieldValue === '';
            return {
              value: isEmpty && isRequired ? 'This field is mandatory' : fieldValue || '',
              error: combineMessage(row?.errors?.[fieldKey]?.msg, row?.warning?.[fieldKey]?.msg)
            };
          });

          const rowHeight = getRowHeight({
            label: columnLables[key]?.name,
            labelWidth,
            cells: valueCells,
            valueWidth
          });

          return {
            rowId: isMultiFunds && isComparisonReport ? index + 2 : index + 1,
            height: rowHeight,
            cells: [
              {
                type: 'header',
                text: `${value.columnLables[key]?.order}`
              },
              {
                type:
                  (data[0]?.report_id &&
                    data[0]?.[key] !== null &&
                    schema.types[key]?.has_drill_down_feature) ||
                  value.columnLables[key].name.includes('*')
                    ? 'customHeader'
                    : 'header',
                text: value.columnLables[key].name || '',
                render: () => (
                  <CustomHeaderWithDrillDown
                    title={value.columnLables[key].name}
                    reportId={data[0]?.report_id}
                    fundId={data[0].fund_id}
                    columnName={key}
                    tableName="GEOGRAPHICAL_FOCUS"
                    tableId={data[0]?.id}
                    hasDrillDown={!isMultiFunds && !!schema.types[key]?.has_drill_down_feature}
                  />
                ),
                style: {
                  'justify-content': 'flex-start',
                  'white-space': 'inherit'
                }
              },
              ...cells
            ]
          };
        })
      ];
    });
  }, [data, isStaticTemplate, isMultiFunds, funds, isComparisonReport]);

  return { tables, columns, onChange };
}
